/**
 * QualGear product gallery — thumbnail swap + zoom-modal sync + variant sync.
 *
 * Purpose-built (not the vendor slider); video playback and the zoom modal
 * itself are handled by existing global custom elements (<deferred-media>,
 * <modal-dialog>/<modal-opener> from global.js) — this file only owns
 * "which slide is the active one" across the main viewer, the thumbnail
 * strip, and the zoom modal.
 *
 * W5-FLOW-D04 fix (2026-07-31): selecting a variant never moved the main
 * image. Root cause was that the vendor's own variant-swap machinery
 * (product-info.js `updateMedia()`) only knows how to drive the vendor's
 * `<media-gallery>` custom element (`this.querySelector('media-gallery')
 * ?.setActiveMedia?.(...)`) — qg-product-gallery.liquid is a purpose-built
 * replacement that never registers that element, so `updateMedia()` was a
 * silent no-op every time. Rather than resurrect `<media-gallery>` (which
 * would mean forking the vendor slider this component was built to avoid),
 * this hooks the same `variant-change` pub/sub event product-info.js
 * already publishes on every successful variant swap
 * (`publish(PUB_SUB_EVENTS.variantChange, { data: { sectionId, variant } })`
 * in product-info.js's `handleUpdateProductInfo`) and drives our own
 * `activate()` from `variant.featured_media.id` — no edits to
 * product-info.js/product-variant-picker.liquid required.
 */
(function () {
  if (window.qgProductGalleryInit) return;
  window.qgProductGalleryInit = true;

  /*
   * DEF-33 single-player rule. Only one gallery video (main stage or zoom
   * modal) may play at a time, and a slide that is hidden must not keep
   * playing: `hidden` only stops painting — a display:none YouTube/Vimeo
   * iframe or <video> keeps running and keeps producing audio.
   *
   * - Switching slides (thumb click / variant change) pauses every player.
   * - A player that STARTS pauses every other one. Native <video> reports
   *   this through the `play` event; YouTube and Vimeo report it over their
   *   postMessage APIs (YouTube needs the `listening` handshake and the
   *   enablejsapi=1 Shopify already adds; Vimeo needs addEventListener after
   *   its `ready`). Pausing uses the same commands as global.js pauseAllMedia.
   */
  var PLAYER_SCOPE = '[data-qg-product-gallery], .qg-product-gallery__modal';

  function galleryPlayers() {
    var list = [];
    document.querySelectorAll(PLAYER_SCOPE).forEach(function (scope) {
      scope.querySelectorAll('iframe, video').forEach(function (el) { list.push(el); });
    });
    return list;
  }

  function pausePlayer(el) {
    if (el.tagName === 'VIDEO') {
      if (!el.paused) el.pause();
      return;
    }
    if (!el.contentWindow) return;
    if (/youtube/.test(el.src)) {
      el.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
    } else if (/vimeo/.test(el.src)) {
      el.contentWindow.postMessage('{"method":"pause"}', '*');
    }
  }

  function pauseAllExcept(except) {
    galleryPlayers().forEach(function (el) {
      if (el !== except) pausePlayer(el);
    });
  }

  document.addEventListener('play', function (event) {
    var video = event.target;
    if (video.tagName === 'VIDEO' && video.closest(PLAYER_SCOPE)) pauseAllExcept(video);
  }, true);

  function frameFor(source) {
    var frames = galleryPlayers();
    for (var i = 0; i < frames.length; i++) {
      if (frames[i].contentWindow === source) return frames[i];
    }
    return null;
  }

  window.addEventListener('message', function (event) {
    if (!/^https:\/\/(www\.youtube(-nocookie)?\.com|player\.vimeo\.com)$/.test(event.origin)) return;
    var frame = frameFor(event.source);
    if (!frame) return;
    var data = event.data;
    try { data = typeof data === 'string' ? JSON.parse(data) : data; } catch (e) { return; }
    if (!data) return;
    // YouTube: playerState 1 = playing.
    var ytPlaying = (data.event === 'onStateChange' && data.info === 1) ||
      (data.event === 'infoDelivery' && data.info && data.info.playerState === 1);
    if (ytPlaying || data.event === 'play') {
      frame.dataset.qgPlaying = 'true';
      pauseAllExcept(frame);
    }
    if (data.event === 'ready' && /vimeo/.test(frame.src)) {
      frame.contentWindow.postMessage('{"method":"addEventListener","value":"play"}', '*');
    }
  });

  // Subscribe each newly injected gallery iframe to its player's events.
  function subscribeFrame(frame) {
    if (frame.dataset.qgSubscribed) return;
    frame.dataset.qgSubscribed = 'true';
    if (/youtube/.test(frame.src)) {
      var tries = 0;
      var handshake = function () {
        if (!frame.isConnected || tries++ > 20) return;
        frame.contentWindow && frame.contentWindow.postMessage('{"event":"listening","id":1,"channel":"widget"}', '*');
        setTimeout(handshake, 500);
      };
      frame.addEventListener('load', handshake);
      handshake();
    } else if (/vimeo/.test(frame.src)) {
      frame.addEventListener('load', function () {
        frame.contentWindow.postMessage('{"method":"addEventListener","value":"play"}', '*');
      });
    }
  }

  if ('MutationObserver' in window) {
    new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          var frames = node.tagName === 'IFRAME' ? [node] : node.querySelectorAll ? node.querySelectorAll('iframe') : [];
          frames.forEach(function (f) { if (f.closest(PLAYER_SCOPE)) subscribeFrame(f); });
        });
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  function activate(gallery, mediaId) {
    var mainSlides = gallery.querySelectorAll('[data-qg-gallery-slide]');
    var thumbs = gallery.querySelectorAll('[data-qg-gallery-thumb]');

    // DEF-33: leaving a slide must stop its player (see single-player rule).
    var current = gallery.querySelector('.qg-product-gallery__slide--active');
    if (!current || current.getAttribute('data-media-id') !== mediaId) pauseAllExcept(null);

    mainSlides.forEach(function (slide) {
      var isMatch = slide.getAttribute('data-media-id') === mediaId;
      slide.hidden = !isMatch;
      slide.classList.toggle('qg-product-gallery__slide--active', isMatch);
    });

    thumbs.forEach(function (thumb) {
      var isMatch = thumb.getAttribute('data-media-id') === mediaId;
      thumb.classList.toggle('qg-product-gallery__thumb--active', isMatch);
      thumb.setAttribute('aria-current', isMatch ? 'true' : 'false');
    });

    // Keep the zoom modal (rendered outside the gallery element) in sync.
    var modal = document.querySelector('.qg-product-gallery__modal');
    if (modal) {
      modal.querySelectorAll('[data-qg-modal-slide]').forEach(function (slide) {
        var isMatch = slide.getAttribute('data-media-id') === mediaId;
        slide.hidden = !isMatch;
        slide.classList.toggle('qg-product-gallery__modal-slide--active', isMatch);
      });
    }
  }

  // True only when `mediaId` matches a slide this gallery actually has.
  // Guards the "no image on this variant" case (W5-FLOW-D04 requirement 4):
  // some Cat 7 variants carry no assigned media at all, and a stale/foreign
  // featured_media.id must never blank the gallery — leave the main image
  // exactly as it was.
  function galleryHasMedia(gallery, mediaId) {
    var slides = gallery.querySelectorAll('[data-qg-gallery-slide]');
    for (var i = 0; i < slides.length; i++) {
      if (slides[i].getAttribute('data-media-id') === mediaId) return true;
    }
    return false;
  }

  // DEF-33 F-5: the zoom modal's slides live in a <template> that ModalDialog
  // only stamps on first open, so activate()'s modal sync found nothing and the
  // modal always opened on media[0] whatever the shopper had selected. Re-sync
  // from the active main slide once the opener has stamped the template, and
  // stop any page player so nothing keeps playing behind the dialog.
  function syncModalToActive(gallery) {
    var active = gallery.querySelector('.qg-product-gallery__slide--active');
    var modal = document.querySelector('.qg-product-gallery__modal');
    if (!active || !modal) return;
    var mediaId = active.getAttribute('data-media-id');
    modal.querySelectorAll('[data-qg-modal-slide]').forEach(function (slide) {
      var isMatch = slide.getAttribute('data-media-id') === mediaId;
      slide.hidden = !isMatch;
      slide.classList.toggle('qg-product-gallery__modal-slide--active', isMatch);
    });
  }

  document.addEventListener('click', function (event) {
    var opener = event.target.closest('.qg-product-gallery__zoom-opener');
    if (!opener) return;
    var gallery = opener.closest('[data-qg-product-gallery]');
    if (!gallery) return;
    pauseAllExcept(null);
    // ModalDialog.show() stamps the template synchronously inside the opener's
    // own click handler; the next frame is after it either way.
    requestAnimationFrame(function () { syncModalToActive(gallery); });
  });

  document.addEventListener('click', function (event) {
    var thumb = event.target.closest('[data-qg-gallery-thumb]');
    if (!thumb) return;
    var gallery = thumb.closest('[data-qg-product-gallery]');
    if (!gallery) return;
    activate(gallery, thumb.getAttribute('data-media-id'));
  });

  // Launch review 2026-09-30: touch swipe on the main image. Phones had tap-only
  // thumbnails (the flows lane found swiping did nothing). A horizontal swipe
  // (>= 40px, clearly more horizontal than vertical) steps to the next/previous
  // slide in thumbnail order via activate(), so the single-player and modal-sync
  // rules above still apply. Touch pointers only; mouse and pen keep the existing
  // controls. No wrap-around: the first and last slides are ends. Vertical page
  // scrolling is untouched (touch-action: pan-y on the stage, qg-product.css).
  var swipeStart = null;
  document.addEventListener('pointerdown', function (event) {
    if (event.pointerType !== 'touch') return;
    var main = event.target.closest('.qg-product-gallery__main');
    if (!main || event.target.closest('button, a, iframe, video, model-viewer')) return;
    swipeStart = { x: event.clientX, y: event.clientY, main: main };
  }, { passive: true });

  document.addEventListener('pointerup', function (event) {
    var start = swipeStart;
    swipeStart = null;
    if (!start || event.pointerType !== 'touch') return;
    var dx = event.clientX - start.x;
    var dy = event.clientY - start.y;
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    var gallery = start.main.closest('[data-qg-product-gallery]');
    if (!gallery) return;
    var slides = Array.prototype.slice.call(gallery.querySelectorAll('[data-qg-gallery-slide]'));
    var index = slides.findIndex(function (s) { return s.classList.contains('qg-product-gallery__slide--active'); });
    var next = index + (dx < 0 ? 1 : -1);
    if (index < 0 || next < 0 || next >= slides.length) return;
    var mediaId = slides[next].getAttribute('data-media-id');
    activate(gallery, mediaId);
    var thumb = gallery.querySelector('[data-qg-gallery-thumb][data-media-id="' + mediaId + '"]');
    if (thumb && thumb.scrollIntoView) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, { passive: true });

  document.addEventListener('pointercancel', function () { swipeStart = null; }, { passive: true });

  // Vendor pub/sub (global.js) — defined by the time this deferred script
  // runs (global.js loads in <head>, ahead of every deferred product script).
  // Guarded defensively rather than assumed, so a load-order change degrades
  // to "thumb click still works" instead of a hard error.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, function (payload) {
      var eventData = payload && payload.data;
      var variant = eventData && eventData.variant;
      if (!variant) return;

      var featuredMedia = variant.featured_media;
      if (!featuredMedia || featuredMedia.id == null) return; // no image on this variant — leave main image unchanged

      var gallery = eventData.sectionId
        ? document.getElementById('QgGallery-' + eventData.sectionId)
        : null;
      if (!gallery) return;

      var mediaId = String(featuredMedia.id);
      if (!galleryHasMedia(gallery, mediaId)) return;

      activate(gallery, mediaId);
    });
  }
})();
